import type { EventCallback, OrderPilotCommand, WidgetApi } from '../types';

export type QueueHost = {
    __orderpilotQueue?: unknown[][];
    OrderPilot?: ((...args: unknown[]) => void) | undefined;
};

const KNOWN_COMMANDS: ReadonlySet<string> = new Set<OrderPilotCommand>([
    'show',
    'hide',
    'toggle',
    'on',
]);

export function installCommandStub(win: QueueHost): void {
    if (typeof win.OrderPilot === 'function') {
        return;
    }

    const queue = getOrCreateQueue(win);
    win.OrderPilot = (...args: unknown[]) => {
        queue.push(args);
    };
}

export function installLiveDispatcher(win: QueueHost, api: WidgetApi): void {
    win.OrderPilot = (...args: unknown[]) => {
        executeCall(args, api);
    };
}

export function getOrCreateQueue(win: QueueHost): unknown[][] {
    const queue = win.__orderpilotQueue;

    if (Array.isArray(queue)) {
        return queue;
    }

    const created: unknown[][] = [];
    win.__orderpilotQueue = created;

    return created;
}

export function drainQueue(queue: unknown[][], api: WidgetApi): void {
    for (const call of queue.splice(0)) {
        executeCall(call, api);
    }
}

function executeCall(call: unknown[], api: WidgetApi): void {
    const [command, ...args] = call;

    if (typeof command !== 'string' || !KNOWN_COMMANDS.has(command)) {
        warn(`Unknown command: ${stringify(command)}`);

        return;
    }

    switch (command as OrderPilotCommand) {
        case 'show':
            api.show();
            break;
        case 'hide':
            api.hide();
            break;
        case 'toggle':
            api.toggle();
            break;
        case 'on': {
            const [event, callback] = args;

            if (event === 'ready' && typeof callback === 'function') {
                api.on('ready', callback as EventCallback);
            } else {
                warn('OrderPilot("on") expects ("ready", function)');
            }

            break;
        }
    }
}

function warn(message: string): void {
    console.warn(`[OrderPilot] ${message}`);
}

function stringify(value: unknown): string {
    return typeof value === 'string' ? `"${value}"` : String(value);
}
